import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Shield, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export const ReferrerVerificationStep = ({ formData, updateFormData, onNext }) => {
  const [firstName, setFirstName] = useState(formData.referrerFirstName || '');
  const [lastName, setLastName] = useState(formData.referrerLastName || '');
  const [email, setEmail] = useState(formData.referrerEmail || '');
  const [phone, setPhone] = useState(formData.referrerPhone || '');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!firstName || !lastName) {
      toast.error('Please enter your name');
      return;
    }

    setSending(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/referrals/verify-referrer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setCodeSent(true);
        if (data.dev_code) {
          toast.success(`Code sent! FOR TESTING: ${data.dev_code}`, { duration: 10000 });
        } else {
          toast.success('Verification code sent! Check your email.');
        }
      } else {
        toast.error(data.detail || 'Failed to send code');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setVerifying(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/referrals/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        updateFormData({
          referrerFirstName: firstName,
          referrerLastName: lastName,
          referrerEmail: email,
          referrerPhone: `+1${phoneDigits}`,
          referrerVerified: true,
          verificationToken: data.verification_token
        });
        toast.success('Email verified! Let\'s add your referrals...');
        setTimeout(() => onNext(), 800);
      } else {
        toast.error(data.detail || 'Invalid code. Please try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-4">
          <Mail className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0B2240] mb-2">
          Your Information
        </h2>
        <p className="text-[#4C5A70]">
          Let us know who to thank for this referral
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">First Name *</Label>
          <Input data-testid="referrer-verification-step-input-5"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
            disabled={codeSent}
            className="mt-2"
          />
        </div>
        <div>
          <Label htmlFor="lastName">Last Name *</Label>
          <Input data-testid="referrer-verification-step-input-4"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            disabled={codeSent}
            className="mt-2"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="email">Your Email *</Label>
        <Input data-testid="referrer-verification-step-input-3"
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="john@example.com"
          disabled={codeSent}
          className="mt-2"
        />
      </div>

      <div>
        <Label htmlFor="phone">Your Phone *</Label>
        <Input data-testid="referrer-verification-step-input-2"
          id="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          placeholder="(555) 123-4567"
          disabled={codeSent}
          className="mt-2"
        />
      </div>

      {!codeSent ? (
        <Button data-testid="referrer-verification-step-button-2"
          onClick={handleSendCode}
          disabled={sending || !email || !firstName || !lastName || !phone}
          className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
        >
          {sending ? 'Sending...' : 'Send Verification Code'}
        </Button>
      ) : (
        <div className="space-y-4">
          <div>
            <Label htmlFor="code">Verification Code</Label>
            <p className="text-sm text-[#4C5A70] mb-2">
              Enter the 6-digit code we sent to {email}
            </p>
            <Input data-testid="referrer-verification-step-input-1"
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              className="text-center text-2xl tracking-wider"
            />
          </div>

          <Button data-testid="referrer-verification-step-button-1"
            onClick={handleVerifyCode}
            disabled={verifying || code.length !== 6}
            className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
          >
            {verifying ? 'Verifying...' : 'Verify Email & Continue'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      <div className="bg-[#F7F6F2] p-4 rounded-lg border-l-4 border-[#D4AF37] mt-6">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#0B2240] mb-1">Why verify your email?</p>
            <p className="text-sm text-[#4C5A70]">
              Email verification helps us ensure quality referrals and allows us to send you a confirmation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};